import React, { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { API_BASE } from "@/services/api";
import { useAuth } from "@/auth/AuthContext";
import {
  Gift, Copy, Check, Share2, Users, Sparkles, ArrowRight, Coins, Clock,
} from "lucide-react";

const WA_TEXT =
  "I'm prepping for UPSC with CLARIAS — the decision-intelligence layer for aspirants. " +
  "Join free using my link and we both level up:";

export default function ReferEarnPage() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);
  const [redeeming, setRedeeming] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API_BASE}/referrals/me`);
      setData(data);
    } catch (e) {
      toast.error("Couldn't load your referral details.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const copy = async (text, label = "Link") => {
    let ok = false;
    try {
      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(text);
        ok = true;
      }
    } catch (e) { /* fall through to legacy */ }
    if (!ok) {
      try {
        const ta = document.createElement("textarea");
        ta.value = text;
        ta.style.position = "fixed";
        ta.style.opacity = "0";
        document.body.appendChild(ta);
        ta.focus();
        ta.select();
        ok = document.execCommand("copy");
        document.body.removeChild(ta);
      } catch (e) { ok = false; }
    }
    if (ok) {
      setCopied(true);
      toast.success(`${label} copied!`);
      setTimeout(() => setCopied(false), 1800);
    } else {
      toast.error("Couldn't copy automatically — long-press to copy the link.");
    }
  };

  const shareWhatsApp = () => {
    const url = `https://wa.me/?text=${encodeURIComponent(`${WA_TEXT} ${data.link}`)}`;
    window.open(url, "_blank");
  };

  const nativeShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({ title: "Join me on CLARIAS", text: WA_TEXT, url: data.link });
      } catch (e) { /* cancelled */ }
    } else {
      copy(data.link);
    }
  };

  const redeem = async () => {
    setRedeeming(true);
    try {
      const { data: res } = await axios.post(`${API_BASE}/referrals/redeem`);
      toast.success(res.message || "PRO unlocked!");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Redemption failed.");
    } finally {
      setRedeeming(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#0B1020]" data-testid="refer-loading">
        <div className="animate-pulse text-slate-400">Loading your rewards…</div>
      </div>
    );
  }
  if (!data) return null;

  const pct = Math.min(100, Math.round((data.credits % data.credits_per_month) / data.credits_per_month * 100));
  const inCycle = data.credits % data.credits_per_month;

  return (
    <div className="min-h-screen bg-[#0B1020] text-white" data-testid="refer-earn-page">
      <div className="max-w-5xl mx-auto px-5 py-10 sm:py-14">
        {/* Hero */}
        <div className="flex items-start gap-4 mb-10">
          <div className="h-12 w-12 rounded-2xl bg-amber-500/15 border border-amber-400/30 flex items-center justify-center shrink-0">
            <Gift className="h-6 w-6 text-amber-400" />
          </div>
          <div>
            <h1 className="text-3xl sm:text-4xl font-bold tracking-tight" style={{ fontFamily: "Outfit, sans-serif" }}>
              Refer &amp; Earn PRO
            </h1>
            <p className="text-slate-400 mt-2 text-sm sm:text-base max-w-2xl">
              Invite fellow aspirants. When a friend upgrades to PRO, you earn{" "}
              <span className="text-amber-400 font-semibold">{data.credits_per_referral} credits</span>.
              Collect <span className="text-amber-400 font-semibold">{data.credits_per_month}</span> and unlock{" "}
              <span className="text-amber-400 font-semibold">1 month of PRO — free</span>. Credits never expire.
            </p>
          </div>
        </div>

        <div className="grid md:grid-cols-2 gap-6">
          {/* Credits + progress */}
          <div className="rounded-2xl bg-[#111827] border border-white/10 p-6" data-testid="credits-card">
            <div className="flex items-center gap-2 text-slate-400 text-sm">
              <Coins className="h-4 w-4 text-amber-400" /> Your credits
            </div>
            <div className="mt-2 flex items-end gap-2">
              <span className="text-5xl font-bold text-white" data-testid="credits-balance">{data.credits}</span>
              <span className="text-slate-500 mb-1.5 text-sm">credits</span>
            </div>

            <div className="mt-5">
              <div className="flex justify-between text-xs text-slate-400 mb-1.5">
                <span>{inCycle} / {data.credits_per_month} to next free month</span>
                <span>{data.months_redeemable > 0 ? `${data.months_redeemable} month(s) ready` : `${data.credits_to_next_reward} to go`}</span>
              </div>
              <div className="h-2.5 rounded-full bg-white/10 overflow-hidden">
                <div className="h-full bg-gradient-to-r from-amber-500 to-amber-300 transition-all duration-500"
                     style={{ width: `${data.months_redeemable > 0 ? 100 : pct}%` }} data-testid="credits-progress" />
              </div>
            </div>

            <button
              onClick={redeem}
              disabled={!data.can_redeem || redeeming}
              data-testid="redeem-btn"
              className={`mt-6 w-full rounded-xl py-3 font-semibold flex items-center justify-center gap-2 transition
                ${data.can_redeem
                  ? "bg-amber-500 hover:bg-amber-400 text-[#0B1020]"
                  : "bg-white/5 text-slate-500 cursor-not-allowed border border-white/10"}`}
            >
              <Sparkles className="h-4 w-4" />
              {redeeming ? "Unlocking…" : data.can_redeem ? "Redeem 300 → 1 Month PRO" : `Need ${data.credits_to_next_reward} more credits`}
            </button>
          </div>

          {/* Share link */}
          <div className="rounded-2xl bg-[#111827] border border-white/10 p-6" data-testid="share-card">
            <div className="flex items-center gap-2 text-slate-400 text-sm">
              <Share2 className="h-4 w-4 text-sky-400" /> Your referral link
            </div>
            <div className="mt-3 text-xs text-slate-500">Referral code</div>
            <div className="mt-1 flex items-center gap-2">
              <span className="text-2xl font-mono font-bold tracking-wider text-white" data-testid="referral-code">{data.code}</span>
              <button onClick={() => copy(data.code, "Code")} className="text-slate-400 hover:text-white" data-testid="copy-code-btn">
                <Copy className="h-4 w-4" />
              </button>
            </div>

            <div className="mt-4 flex items-stretch gap-2">
              <div className="flex-1 rounded-lg bg-black/30 border border-white/10 px-3 py-2.5 text-xs text-slate-300 truncate flex items-center" data-testid="referral-link">
                {data.link}
              </div>
              <button onClick={() => copy(data.link)} data-testid="copy-link-btn"
                className="rounded-lg bg-white/10 hover:bg-white/20 px-3 flex items-center gap-1.5 text-sm font-medium">
                {copied ? <Check className="h-4 w-4 text-emerald-400" /> : <Copy className="h-4 w-4" />}
                {copied ? "Copied" : "Copy"}
              </button>
            </div>

            <div className="mt-3 grid grid-cols-2 gap-2">
              <button onClick={shareWhatsApp} data-testid="share-whatsapp-btn"
                className="rounded-lg bg-emerald-600 hover:bg-emerald-500 py-2.5 text-sm font-semibold flex items-center justify-center gap-2">
                <Share2 className="h-4 w-4" /> WhatsApp
              </button>
              <button onClick={nativeShare} data-testid="share-more-btn"
                className="rounded-lg bg-white/10 hover:bg-white/20 py-2.5 text-sm font-semibold flex items-center justify-center gap-2">
                <Share2 className="h-4 w-4" /> More
              </button>
            </div>
          </div>
        </div>

        {/* How it works */}
        <div className="mt-8 rounded-2xl bg-[#111827] border border-white/10 p-6">
          <div className="text-sm font-semibold text-slate-300 mb-4">How it works</div>
          <div className="grid sm:grid-cols-3 gap-4">
            {[
              { icon: Share2, t: "Share your link", d: "Send your code to fellow aspirants." },
              { icon: Users, t: "They go PRO", d: `You earn ${data.credits_per_referral} credits on their first PRO purchase.` },
              { icon: Gift, t: "Unlock free PRO", d: `${data.credits_per_month} credits = 1 free month. Repeat, no limit.` },
            ].map((s, i) => (
              <div key={i} className="flex gap-3">
                <div className="h-9 w-9 rounded-lg bg-white/5 border border-white/10 flex items-center justify-center shrink-0">
                  <s.icon className="h-4 w-4 text-amber-400" />
                </div>
                <div>
                  <div className="text-sm font-medium text-white flex items-center gap-1">
                    <span className="text-amber-400">{i + 1}.</span> {s.t}
                  </div>
                  <div className="text-xs text-slate-400 mt-0.5">{s.d}</div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Referral list */}
        <div className="mt-8 rounded-2xl bg-[#111827] border border-white/10 p-6" data-testid="referrals-list">
          <div className="flex items-center justify-between mb-4">
            <div className="text-sm font-semibold text-slate-300">Your referrals</div>
            <div className="flex gap-4 text-xs text-slate-400">
              <span data-testid="stat-total">Total: <b className="text-white">{data.stats.total}</b></span>
              <span data-testid="stat-converted">Converted: <b className="text-emerald-400">{data.stats.converted}</b></span>
              <span data-testid="stat-pending">Pending: <b className="text-amber-400">{data.stats.pending}</b></span>
            </div>
          </div>

          {data.referrals.length === 0 ? (
            <div className="text-center py-10 text-slate-500 text-sm" data-testid="referrals-empty">
              <Users className="h-8 w-8 mx-auto mb-3 opacity-40" />
              No referrals yet. Share your link to get started!
            </div>
          ) : (
            <div className="divide-y divide-white/5">
              {data.referrals.map((r, i) => (
                <div key={i} className="flex items-center justify-between py-3">
                  <div className="text-sm text-slate-300">{r.email}</div>
                  <div className="flex items-center gap-3">
                    {r.status === "converted" ? (
                      <span className="text-xs px-2 py-0.5 rounded-full bg-emerald-500/15 text-emerald-400 border border-emerald-400/20 flex items-center gap-1">
                        <Check className="h-3 w-3" /> +{r.credits_awarded}
                      </span>
                    ) : (
                      <span className="text-xs px-2 py-0.5 rounded-full bg-amber-500/10 text-amber-400 border border-amber-400/20 flex items-center gap-1">
                        <Clock className="h-3 w-3" /> Pending
                      </span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
